import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { songsApi } from '../../api/songs';
import { votesApi } from '../../api/votes';
import { subsetsApi } from '../../api/subsets';
import { SongCard } from '../SongCard/SongCard';
import type { SongSummary, SubsetWithSongs } from '../../types';

interface SongGridProps {
  onSelectSong: (song: SongSummary) => void;
  subsetSongIds?: number[]; // Optional: filter to specific subset
  subsetId?: number; // Optional: subset ID for fetching votes
  voteRefreshKey?: number; // Optional: increment to trigger vote reload
}

export function SongGrid({ onSelectSong, subsetSongIds, subsetId, voteRefreshKey }: SongGridProps) {
  const [songs, setSongs] = useState<SongSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voteRanks, setVoteRanks] = useState<Map<number, number>>(new Map());
  const [voteIsSubmitted, setVoteIsSubmitted] = useState<boolean>(true);
  // Map of songId -> array of category indices from user's own votes
  const [userSpecialCategoryIcons, setUserSpecialCategoryIcons] = useState<Map<number, number[]>>(new Map());
  const [subset, setSubset] = useState<SubsetWithSongs | null>(null);

  useEffect(() => {
    loadSongs();
  }, [subsetSongIds]);

  useEffect(() => {
    if (subsetId !== undefined) {
      loadSubset();
      loadVotes();
    } else {
      setSubset(null);
      setVoteRanks(new Map());
      setUserSpecialCategoryIcons(new Map());
    }
  }, [subsetId, voteRefreshKey]);

  const loadSubset = async () => {
    if (subsetId === undefined) return;

    try {
      const subsetData = await subsetsApi.getSubsetById(subsetId);
      setSubset(subsetData);
    } catch (error) {
      console.error('Failed to load subset:', error);
      setSubset(null);
    }
  };

  const loadVotes = async () => {
    if (subsetId === undefined) return;

    try {
      const response = await votesApi.getUserVote(subsetId);
      if (response.hasVoted && response.vote) {
        // Create a map of songId -> rank (1-based)
        const rankMap = new Map<number, number>();
        response.vote.rankedSongIds.forEach((songId, index) => {
          rankMap.set(songId, index + 1);
        });
        setVoteRanks(rankMap);
        setVoteIsSubmitted(response.vote.isSubmitted);

        // Create a map of songId -> array of special category indices
        const specialMap = new Map<number, number[]>();
        response.vote.specialCategoryVotes.forEach((songId, categoryIndex) => {
          if (songId !== null) {
            const existing = specialMap.get(songId) || [];
            specialMap.set(songId, [...existing, categoryIndex]);
          }
        });
        setUserSpecialCategoryIcons(specialMap);
      } else {
        setVoteRanks(new Map());
        setVoteIsSubmitted(true);
        setUserSpecialCategoryIcons(new Map());
      }
    } catch (error) {
      console.error('Failed to load votes:', error);
      setVoteRanks(new Map());
      setVoteIsSubmitted(true);
      setUserSpecialCategoryIcons(new Map());
    }
  };


  const loadSongs = async () => {
    setLoading(true);
    setError(null);

    try {
      const allSongs = await songsApi.getAllSongs();

      // Filter to subset if specified
      let filteredSongs = allSongs;
      if (subsetSongIds !== undefined) {
        // If subsetSongIds is provided (even if empty), filter
        if (subsetSongIds.length === 0) {
          // Empty subset - show no songs
          filteredSongs = [];
        } else {
          // Subset with songs - filter and sort
          const songIdSet = new Set(subsetSongIds);
          filteredSongs = allSongs.filter(song => songIdSet.has(song.id));

          // Sort by subset order
          filteredSongs.sort((a, b) => {
            const aIndex = subsetSongIds.indexOf(a.id);
            const bIndex = subsetSongIds.indexOf(b.id);
            return aIndex - bIndex;
          });
        }
      }

      setSongs(filteredSongs);
    } catch (err) {
      setError((err as Error).message || 'Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-monster-blue mx-auto mb-4" />
          <p className="text-gray-400">Loading songs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="card max-w-md text-center">
          <p className="text-monster-crimson mb-4">Error loading songs</p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button onClick={loadSongs} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (songs.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="card max-w-md text-center">
          <p className="text-gray-400 mb-2">No songs found</p>
          <p className="text-sm text-gray-500">
            Ask an admin to scan the media folder to add songs
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {songs.map((song) => {
        // Only show user's own special category selections
        const specialCategories = userSpecialCategoryIcons.get(song.id) || [];

        return (
          <SongCard
            key={song.id}
            song={song}
            onClickCard={onSelectSong}
            voteRank={voteRanks.get(song.id)}
            voteIsSubmitted={voteIsSubmitted}
            specialCategoryIndices={specialCategories}
            subset={subset}
          />
        );
      })}
    </div>
  );
}
